// §6.3 / §12 — the house launcher: one centred command palette, search-first.
//
// With nothing typed it is a list of apps, because that is what a launcher is for. The moment
// anything is typed it becomes §12's index — apps, files, open windows and settings, grouped by
// source with the best group first — and `Enter` "launches or focuses" whichever of those the
// selection turns out to be. Which of the two that means is the kernel's answer, not this file's.
import { useEffect, useMemo, useRef, useState } from 'react';
import { Search } from '@/assets/icons';
import { ProfileMenu } from '@/host/ProfileMenu';
import { COPY } from '@/copy';
import type { SearchEntry } from '@/kernel/search';
import { useKernel, useSearch, useStoreValue, useUnhealthyApps } from '@/kernel/react';

export function Launcher() {
  const kernel = useKernel();
  const open = useStoreValue(kernel.shellui.store, (s) => s.overlay === 'launcher');
  const unhealthy = useUnhealthyApps();
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Recomputed each time the palette opens: an app installed since the last open must show up
  // without a reload (§9.6).
  //
  // §29.1 (D-369) — twenty Arcade titles in one flat list stops being scannable. The order and the
  // headers come from the kernel so three launchers cannot disagree about them.
  const sections = useMemo(() => (open ? kernel.launch.grouped() : []), [kernel, open]);
  const searching = query.trim().length > 0;
  const { groups, flat } = useSearch(query, open);

  /**
   * With nothing typed, the app list *is* the result list — one shape for the keys to move over.
   *
   * Built **once**, and the sections below render the very same entry objects: `row()` finds a
   * result's position with `indexOf`, so a section that rebuilt its own entries would hand every
   * one of them an index of −1 and the arrow keys would stop moving.
   */
  const listed = useMemo(
    () =>
      sections.map((section) => ({
        section,
        entries: section.apps.map((app) => ({
          source: 'app' as const,
          id: app.id,
          label: app.name,
        })),
      })),
    [sections],
  );

  const rows: readonly SearchEntry[] = useMemo(
    () => (searching ? flat : listed.flatMap((entry) => entry.entries)),
    [searching, flat, listed],
  );

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setSelected(0);
    inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    setSelected((current) => Math.min(current, Math.max(0, rows.length - 1)));
  }, [rows.length]);

  if (!open) return null;

  const activate = (entry: SearchEntry | undefined): void => {
    if (!entry) return;
    kernel.shellui.closeOverlay();
    kernel.activateSearchResult(entry);
  };

  /** Where a row sits in the flattened list, so the arrow keys and the mouse agree. */
  const indexOf = (entry: SearchEntry): number => rows.indexOf(entry);

  const row = (entry: SearchEntry) => {
    const index = indexOf(entry);
    return (
      <li key={`${entry.source}:${entry.id}`}>
        <button
          type="button"
          className="house-launcher-result"
          data-testid={`launcher-result-${entry.id}`}
          data-source={entry.source}
          data-selected={String(index === selected)}
          data-health={
            entry.source === 'app' && unhealthy.has(entry.id) ? 'unhealthy' : 'ok'
          }
          onPointerEnter={() => setSelected(index)}
          onClick={() => activate(entry)}
        >
          <span className="house-launcher-label">{entry.label}</span>
          {entry.detail !== undefined && (
            <span className="house-launcher-detail">{entry.detail}</span>
          )}
        </button>
      </li>
    );
  };

  return (
    <div
      className="house-launcher-backdrop"
      data-testid="launcher"
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) kernel.shellui.closeOverlay();
      }}
    >
      <div className="house-launcher" role="dialog" aria-label="Launcher">
        <div className="house-launcher-field">
          <Search size={16} aria-hidden />
          <input
            ref={inputRef}
            type="text"
            className="house-launcher-input"
            data-testid="launcher-input"
            placeholder={COPY.chrome.startSearch}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown') {
                event.preventDefault();
                setSelected((i) => Math.min(i + 1, rows.length - 1));
              } else if (event.key === 'ArrowUp') {
                event.preventDefault();
                setSelected((i) => Math.max(i - 1, 0));
              } else if (event.key === 'Enter') {
                event.preventDefault();
                activate(rows[selected]);
              } else if (event.key === 'Escape') {
                event.preventDefault();
                kernel.shellui.closeOverlay();
              }
            }}
          />
        </div>

        <div className="house-launcher-results" data-testid="launcher-results">
          {searching ? (
            groups.map((group) => (
              <section
                key={group.source}
                className="house-launcher-group"
                data-testid={`launcher-group-${group.source}`}
              >
                <h3 className="house-launcher-group-name">{COPY.search[group.source]}</h3>
                <ul className="house-launcher-list">
                  {group.results.map((result) => row(result.entry))}
                </ul>
              </section>
            ))
          ) : (
            listed.map(({ section, entries }) => (
              <section
                key={`${section.category ?? ''}/${section.group ?? ''}`}
                className="house-launcher-group"
                data-testid={`launcher-section-${section.group ?? section.category ?? 'apps'}`}
              >
                {section.category === null ? null : (
                  <h3 className="house-launcher-group-name">{section.group ?? section.category}</h3>
                )}
                <ul className="house-launcher-list">{entries.map(row)}</ul>
              </section>
            ))
          )}
          {rows.length === 0 ? (
            <p className="house-launcher-empty" data-testid="launcher-no-results">
              {COPY.chrome.startNoResults}
            </p>
          ) : null}
        </div>

        {/* D-346 — who you are, and the two things you do about it. Shared across all three
            skins, so a sign-out button cannot quietly go missing from one of them. */}
        <ProfileMenu prefix="house" />
      </div>
    </div>
  );
}
